import React, { useEffect, useState } from "react";
import { useAuth } from "../../context/authContext";
import { Alert, Box, Button, CircularProgress, IconButton, Tab, Tabs, Tooltip, Typography, Divider } from "@mui/material";
import PageTitle from "../../components/PageTitle/PageTitle";
import ExpandableDataGrid from "../../components/ExpandableDataGrid/ExpandableDataGrid";
import SummarizeIcon from "@mui/icons-material/Summarize";
import { useTranslation } from "react-i18next";
import { AddCircleOutline } from "@mui/icons-material";
import "./Services.css";
import NotAuthorized from "../../components/NotAuthorized/NotAuthorized";
import CreateCaseForm from "../../components/ContactCCRForm/CreateCaseForm";
import { formatDate } from "../../utilities/globalServiceUtils";
import Swal from "sweetalert2";
import { getWorkorderReport } from "../../services/servicesService";
import { useCasesData, useWorkordersData } from "../../hooks/useServiceRecords";
import DocumentViewModal from "../../components/DocumentViewModal/DocumentViewModal";
import WorkorderReportsModal from "../../components/WorkorderReportsModal/WorkorderReportsModal";

const Services = () => {
	const { hasPermission } = useAuth();
	const [activeTab, setActiveTab] = useState(0);
	const { t } = useTranslation();
	const [createCaseOpen, setCreateCaseOpen] = useState(false);
	const [reportLoadingWo, setReportLoadingWo] = useState(null);
	const [reportLoadingType, setReportLoadingType] = useState(null);
	const [autoExpandWoId, setAutoExpandWoId] = useState(null);
	const [reportListRow, setReportListRow] = useState(null);
	const [reportDocument, setReportDocument] = useState(null);
	const [reportModalOpen, setReportModalOpen] = useState(false);
	const { cases, casesLoading } = useCasesData();
	const { workorders, workordersLoading } = useWorkordersData();
	const loading = casesLoading || workordersLoading;
	const handleTabChange = (event, newValue) => {
		setActiveTab(newValue);
	};


	const columns_cases = [
		{ field: "case_number", headerName: t('TABLE_HEADERS.CASE_NUMBER'), width: 150 },
		{ field: "case_title", headerName: t('TABLE_HEADERS.TITLE'), flex: 1.5 },
		{ field: "case_type", headerName: t('TABLE_HEADERS.TYPE'), width: 150, filterable: true },
		{ field: "case_status", headerName: t('TABLE_HEADERS.STATUS'), width: 140, filterable: true },
		{ field: "case_priority", headerName: t('TABLE_HEADERS.PRIORITY'), width: 140, filterable: true },
		{
			field: "created_date", headerName: t('TABLE_HEADERS.CREATED_DATE'), width: 200, valueFormatter: (value) => formatDate(value),
			// Ensure proper date sorting even if value is a string
			sortComparator: (v1, v2) => {
				const d1 = v1 ? new Date(v1).getTime() : 0;
				const d2 = v2 ? new Date(v2).getTime() : 0;
				return d1 - d2;
			},
		},
		{
			field: "resolution_date", headerName: t('TABLE_HEADERS.RESOLUTION_DATE'), width: 200, valueFormatter: (value) => formatDate(value),
			// Ensure proper date sorting even if value is a string
			sortComparator: (v1, v2) => {
				const d1 = v1 ? new Date(v1).getTime() : 0;
				const d2 = v2 ? new Date(v2).getTime() : 0;
				return d1 - d2;
			},
		},
	];

	const columns_wo = [
		{ field: "wo_number", headerName: t('TABLE_HEADERS.WO_NUMBER'), flex: 1 },
		{ field: "wo_type", headerName: t('TABLE_HEADERS.TYPE'), flex: 1, filterable: true },
		{ field: "machine_id", headerName: t('TABLE_HEADERS.MACHINE'), flex: 1, filterable: true },
		{ field: "booked_resource", headerName: t('TABLE_HEADERS.BOOKED_RESOURCE', { defaultValue: 'Booked Resource' }), flex: 1, filterable: true },
		{
			field: "service_start_date", headerName: "Service Start Date", flex: 1, valueFormatter: (value) => formatDate(value),
			sortComparator: (v1, v2) => {
				const d1 = v1 ? new Date(v1).getTime() : 0;
				const d2 = v2 ? new Date(v2).getTime() : 0;
				return d1 - d2;
			},
		},
		{
			field: "service_end_date", headerName: "Service End Date", flex: 1, valueFormatter: (value) => formatDate(value),
			sortComparator: (v1, v2) => {
				const d1 = v1 ? new Date(v1).getTime() : 0;
				const d2 = v2 ? new Date(v2).getTime() : 0;
				return d1 - d2;
			},
		},
		// {
		// 	field: "created_date", headerName: t('TABLE_HEADERS.CREATED_DATE'), flex: 1, valueFormatter: (value) => formatDate(value),
		// 	sortComparator: (v1, v2) => {
		// 		const d1 = v1 ? new Date(v1).getTime() : 0;
		// 		const d2 = v2 ? new Date(v2).getTime() : 0;
		// 		return d1 - d2;
		// 	},
		// },
		// {
		// 	field: "closed_date", headerName: t('TABLE_HEADERS.CLOSED_DATE'), flex: 1, valueFormatter: (value) => formatDate(value),
		// 	sortComparator: (v1, v2) => {
		// 		const d1 = v1 ? new Date(v1).getTime() : 0;
		// 		const d2 = v2 ? new Date(v2).getTime() : 0;
		// 		return d1 - d2;
		// 	},
		// },
		{
			field: "actions", headerName: t('TABLE_HEADERS.ACTIONS'), width: 120, headerAlign: "left",
			align: "left", filterable: false, renderCell: (params) => {
				const isService = String(params?.row?.wo_type || "").toLowerCase() === "service";
				const hasReport = Array.isArray(params?.row?.reports) && params.row.reports.length > 0;
                if (!isService || !hasReport) return null;
				return (
					<Box sx={{ display: "flex", flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', height: '100%' }}>
						<Box sx={{ display: "flex", justifyContent: 'flex-start', alignItems: 'center' }}>
							<Tooltip title={t("UI_TEXT.TOOLTIP_VIEW_SERVICE_REPORT")}>
								<span>
									<IconButton
										size="small"
										color="primary"
										onClick={(e) => {
											e.preventDefault();
											e.stopPropagation();
											const reports = params.row.reports || [];
											if (reports.length === 1) {
												handleViewReport(params.row, reports[0].type, reports[0].id);
											} else {
												setReportListRow(params.row);
											}
										}}
									>
										<SummarizeIcon />
									</IconButton>
								</span>
							</Tooltip>
						</Box>
					</Box>
				);
			}
		},
	];

	const handleViewReport = async (row, reportType, reportId) => {
		try {
			const woNumber = row?.wo_number;
			setReportLoadingWo(woNumber || null);
			setReportLoadingType(reportType || null);
			const resp = await getWorkorderReport(row?.wo_number, reportType, reportId);
			const payload = resp?.data?.data || resp?.data || {};
			const reports = Array.isArray(payload.reports)
				? payload.reports.filter((report) => report?.url)
				: payload.url ? [{ type: reportType, file_name: "Service Report.pdf", url: payload.url }] : [];

			if (reports.length > 0) {
				const selectedReport = reports.find((report) => String(report.id) === String(reportId))
					|| reports.find((report) => String(report.type).toLowerCase() === String(reportType).toLowerCase())
					|| reports[0];

				setReportDocument({
					name: selectedReport.file_name || `${woNumber}-${selectedReport.type}.pdf`,
					url: selectedReport.url,
					content_type: "application/pdf",
					attachment_type: "file",
				});
				setReportModalOpen(true);
			} else {
				Swal.fire({
					icon: 'warning',
					title: 'Report Not Found',
					text: 'The requested work order report could not be found.',
					confirmButtonText: t('GENERAL.OK'),
				});
			}
		} catch (e) {
			Swal.fire({
				icon: 'error',
				title: 'Error',
				text: 'An error occurred while fetching the report. Please try again later.',
				confirmButtonText: t('GENERAL.OK'),
			});
		} finally {
			setReportLoadingWo(null);
			setReportLoadingType(null);
		}
	};

	const handleReportListView = (report) => {
		setReportListRow(null);
		handleViewReport(reportListRow, report.type, report.id);
	};

	const handleCreateCase = () => {
		setCreateCaseOpen(true);
	};

	const customButtonsCases = [
		{
			label: t('LABELS.CREATE_NEW_CASE'),
			onClick: () => {
				handleCreateCase();
			},
			icon: <AddCircleOutline />,
		},
	];

	// Removed Create New Workorder button

	const canSeeCases = !!hasPermission('service_management.view_cases');
	const canSeeWorkOrders = !!hasPermission('service_management.view_workorders');
	const canCreateCase = !!hasPermission('service_management.create_cases');
	//   Build visible tabs dynamically
	const visibleTabs = [];
	if (canSeeCases) visibleTabs.push({ label: t('LABELS.CASES_TICKETS', { defaultValue: "Cases (Tickets)" }), key: 'cases' });
	if (canSeeWorkOrders) visibleTabs.push({ label: t('LABELS.WORK_ORDERS_REPORTS', { defaultValue: "Work Orders & Reports" }), key: 'workorders' });

	useEffect(() => {
		if (activeTab >= visibleTabs.length && visibleTabs.length > 0) {
			setActiveTab(0);
		}
	}, [activeTab, visibleTabs]);



	//   If user can't see either tab, block access entirely
	if (!canSeeCases && !canSeeWorkOrders) {
		return <NotAuthorized />;
	}

	if (loading) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
				<CircularProgress />
			</Box>
		);
	}




	// Ensure valid tab index when permissions change dynamically
	const renderCaseMasterDetails = (caseRow, detailActions) => (
		<Box sx={{
			p: 2, width: '100%', border: "1px solid #e6e8eb",
			borderRadius: 2,
		}}>
			<Box sx={{ mb: 2 }}>
				<Typography variant="body1" fontWeight={"bold"}>Case Details: {caseRow?.case_number}</Typography>
				<Typography variant="body2" color="text.secondary">Please review the case details below</Typography>
			</Box>
			<Box sx={{ display: "flex", flexDirection: "row", justifyContent: "flex-start", alignItems: "center", columnGap: 2 }}>
				<Box sx={{ flex: 1 }}>
					<Typography variant="subtitle2">{t("TABLE_HEADERS.CASE_NUMBER")}</Typography>
					<Typography variant="body2" sx={{ mb: 1 }}>{caseRow?.case_number}</Typography>
				</Box>
				<Box sx={{ flex: 1 }}>
					<Typography variant="subtitle2">{t("TABLE_HEADERS.TYPE")}</Typography>
					<Typography variant="body2" sx={{ mb: 1 }}>{caseRow?.case_type}</Typography>
				</Box>
				<Box sx={{ flex: 1 }}>
					<Typography variant="subtitle2">{t("TABLE_HEADERS.STATUS")}</Typography>
					<Typography variant="body2" sx={{ mb: 1 }}>{caseRow?.case_status}</Typography>
				</Box>
				<Box sx={{ flex: 1 }}>
					<Typography variant="subtitle2">{t("TABLE_HEADERS.PRIORITY")}</Typography>
					<Typography variant="body2" sx={{ mb: 1 }}>{caseRow?.case_priority}</Typography>
				</Box>
			</Box>

			<Divider sx={{ my: 2 }} flexItem />
			<Box sx={{ flex: 1 }}>
				<Typography variant="subtitle2">{t("TABLE_HEADERS.TITLE")}</Typography>
				<Typography variant="body2" sx={{ mb: 1 }}>{caseRow?.case_title}</Typography>
			</Box>
			<Divider sx={{ my: 2 }} flexItem />
			<Box sx={{ flex: 1 }}>
				<Typography variant="subtitle2">{t("TABLE_HEADERS.DESCRIPTIONS")}</Typography>
				<Typography variant="body2" sx={{ whiteSpace: "pre-wrap", mb: 1 }}>{caseRow?.case_description || "-"}</Typography>
			</Box>
			<Divider sx={{ my: 2 }} flexItem />
			<Box sx={{ flex: 1 }}>
				<Typography variant="subtitle2">{t("TABLE_HEADERS.RESOLUTION_DETAILS")}</Typography>
				<Typography variant="body2" sx={{ whiteSpace: "pre-wrap", mb: 1 }}>{caseRow?.case_resolution_details || "-"}</Typography>
			</Box>
			<Divider sx={{ my: 2 }} flexItem />
			<Box sx={{ flex: 1 }}>
				<Typography variant="subtitle2">{t("TABLE_HEADERS.RESOLUTION_NOTES", { defaultValue: "Resolution Notes" })}</Typography>
				<Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{caseRow?.case_resolution_notes || "-"}</Typography>
			</Box>
			<Divider sx={{ my: 2 }} flexItem />
			<Box sx={{ display: "flex", flexDirection: "row", justifyContent: "flex-start", alignItems: "center", columnGap: 2 }}>
				<Box sx={{ flex: 1 }}>
					<Typography variant="subtitle2">{t("TABLE_HEADERS.CREATED_DATE")}</Typography>
					<Typography variant="body2" sx={{ mb: 1 }}>{formatDate(caseRow?.created_date)}</Typography>
				</Box>
				<Box sx={{ flex: 1 }}>
					<Typography variant="subtitle2">{t("TABLE_HEADERS.RESOLUTION_DATE")}</Typography>
					<Typography variant="body2" sx={{ mb: 1 }}>{formatDate(caseRow?.resolution_date) || "-"}</Typography>
				</Box>
			</Box>
			<Divider sx={{ my: 2 }} flexItem />
			<Box sx={{ display: "flex", justifyContent: "flex-end" }}>
				<Button variant="outlined" onClick={detailActions?.collapseRow}>
					{t("BUTTON.CLOSE")}
				</Button>
			</Box>
		</Box>
	);
	const renderWorkorderMasterDetails = (woRow) => (
		<Box sx={{
			p: 2, width: '100%', border: "1px solid #e6e8eb",
			borderRadius: 2,
		}}>
			<Box sx={{ mb: 2 }}>
				<Typography variant="body1" fontWeight={"bold"}>Work Order Details: {woRow?.wo_number}</Typography>
				<Typography variant="body2" color="text.secondary">Please review the work order details below</Typography>
			</Box>
			<Box sx={{ display: "flex", flexDirection: "row", justifyContent: "flex-start", alignItems: "center", columnGap: 2 }}>
				<Box sx={{ flex: 1 }}>
					<Typography variant="subtitle2">{t("TABLE_HEADERS.WO_NUMBER")}</Typography>
					<Typography variant="body2" sx={{ mb: 1 }}>{woRow?.wo_number}</Typography>
				</Box>
				<Box sx={{ flex: 1 }}>
					<Typography variant="subtitle2">{t("TABLE_HEADERS.STATUS")}</Typography>
					<Typography variant="body2" sx={{ mb: 1 }}>{woRow?.wo_status}</Typography>
				</Box>
				<Box sx={{ flex: 1 }}>
					<Typography variant="subtitle2">{t("TABLE_HEADERS.TYPE")}</Typography>
					<Typography variant="body2" sx={{ mb: 1 }}>{woRow?.wo_type}</Typography>
				</Box>
				<Box sx={{ flex: 1 }}>
					<Typography variant="subtitle2">{t("TABLE_HEADERS.MACHINE")}</Typography>
					<Typography variant="body2" sx={{ mb: 1 }}>{woRow?.machine_id || "-"}</Typography>
				</Box>
			</Box>

			<Divider sx={{ my: 2 }} flexItem />
			<Box sx={{ display: "flex", flexDirection: "row", justifyContent: "flex-start", alignItems: "center", columnGap: 2 }}>
				<Box sx={{ flex: 1 }}>
					<Typography variant="subtitle2">{t("TABLE_HEADERS.BOOKED_RESOURCE", { defaultValue: "Booked Resource" })}</Typography>
					<Typography variant="body2" sx={{ mb: 1 }}>{woRow?.booked_resource}</Typography>
				</Box>
			</Box>

			<Divider sx={{ my: 2 }} flexItem />
			<Box sx={{ display: "flex", flexDirection: "row", justifyContent: "flex-start", alignItems: "center", columnGap: 2 }}>
				<Box sx={{ flex: 1 }}>
					<Typography variant="subtitle2">Service Start Date</Typography>
					<Typography variant="body2" sx={{ mb: 1 }}>{formatDate(woRow?.service_start_date)}</Typography>
				</Box>
				<Box sx={{ flex: 1 }}>
					<Typography variant="subtitle2">Service End Date</Typography>
					<Typography variant="body2" sx={{ mb: 1 }}>{formatDate(woRow?.service_end_date)}</Typography>
				</Box>
				<Box sx={{ flex: 1 }}>
					<Typography variant="subtitle2">{t("TABLE_HEADERS.CREATED_DATE")}</Typography>
					<Typography variant="body2" sx={{ mb: 1 }}>{formatDate(woRow?.created_date)}</Typography>
				</Box>
				<Box sx={{ flex: 1 }}>
					<Typography variant="subtitle2">{t("TABLE_HEADERS.CLOSED_DATE")}</Typography>
					<Typography variant="body2" sx={{ mb: 1 }}>{formatDate(woRow?.closed_date) || "-"}</Typography>
				</Box>
			</Box>
			<Divider sx={{ my: 2 }} flexItem />
			<Typography variant="subtitle2">{t("TABLE_HEADERS.DESCRIPTIONS")}</Typography>
			<Typography variant="body2" sx={{ whiteSpace: "pre-wrap", mb: 2 }}>{woRow?.wo_description || "-"}</Typography>
            <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                {String(woRow?.wo_type || "").toLowerCase() === "service" && Array.isArray(woRow?.reports) && woRow.reports.length === 1 && woRow.reports.some((report) => report?.type === "service") && (
                    <Button
					variant="contained"
					onClick={() => handleViewReport(woRow, "service")}
					startIcon={<SummarizeIcon />}
					disabled={reportLoadingWo === woRow?.wo_number}
				>
					{reportLoadingWo === woRow?.wo_number && reportLoadingType === "service" ? t("BUTTON.LOADING", { defaultValue: "Loading..." }) : t("BUTTON.VIEW_SERVICE_REPORT")}
				</Button>
                )}
                {String(woRow?.wo_type || "").toLowerCase() === "service" && Array.isArray(woRow?.reports) && woRow.reports.length === 1 && woRow.reports.some((report) => report?.type === "inspection") && (
                    <Button
					variant="contained"
					onClick={() => handleViewReport(woRow, "inspection")}
					startIcon={<SummarizeIcon />}
					disabled={reportLoadingWo === woRow?.wo_number}
				>
					{reportLoadingWo === woRow?.wo_number && reportLoadingType === "inspection" ? t("BUTTON.LOADING", { defaultValue: "Loading..." }) : "View Inspection Report"}
				</Button>
                )}
            </Box>
		</Box>
	);

	return (
		<div className="services-container">
			<PageTitle title={t('TITLES.SERVICES')} />

			{/*   Permission-based Tabs */}
			<Tabs
				value={activeTab}
				onChange={handleTabChange}
				aria-label="Service Tabs"
				className="order-history-tabs"
			>
				{visibleTabs.map((tab) => (
					<Tab key={tab.key} label={tab.label} />
				))}
			</Tabs>

			{/*   CASES TAB */}
			{visibleTabs[activeTab]?.key === 'cases' && canSeeCases && (
				<>
					{cases.length === 0 && !loading ? (
						<Alert severity="info" className="error-alert" sx={{ marginTop: "10px" }}>
							{t('ALERT_MESSAGES.NO_CASES_FOUND')}
						</Alert>
					) : (
						<ExpandableDataGrid
							key="casesGrid"
							data={cases}
							columns={columns_cases}
							pageSize={5}
							showSearchBar={true}
							checkboxSelection={false}
							customButtons={canCreateCase ? customButtonsCases : []}
							rowIdField="id"
							sortBy={[{ field: "service_start_date", sort: "desc" }]}
							masterDetails={renderCaseMasterDetails}
							allowMultipleExpand={false}
						/>
					)}

				</>
			)}

			{/*   WORK ORDERS TAB */}
			{visibleTabs[activeTab]?.key === 'workorders' && canSeeWorkOrders && (
				<>
					{workorders.length === 0 && !loading ? (
						<Alert severity="info" className="error-alert" sx={{ marginTop: "10px" }}>
							{t('ALERT_MESSAGES.NO_WORKORDERS_FOUND')}
						</Alert>
					) : (
						<ExpandableDataGrid
							key="workordersGrid"
							data={workorders}
							columns={columns_wo}
							pageSize={5}
							showSearchBar={true}
							checkboxSelection={false}
							customButtons={[]}
							rowIdField="id"
							sortBy={[{ field: "created_date", sort: "desc" }]}
							masterDetails={renderWorkorderMasterDetails}
							allowMultipleExpand={false}
						/>
					)}
				</>
			)}

			{/* Create Case Modal */}
			<CreateCaseForm open={createCaseOpen} setOpen={setCreateCaseOpen} />
			<DocumentViewModal
				open={reportModalOpen}
				setOpen={setReportModalOpen}
				document={reportDocument}
				onClose={() => { setReportModalOpen(false); setReportDocument(null); }}
				displayName={reportDocument?.name}
				showDownloadButton={false}
			/>
			<WorkorderReportsModal
				open={!!reportListRow}
				onClose={() => setReportListRow(null)}
				woNumber={reportListRow?.wo_number}
				reports={reportListRow?.reports || []}
				onView={handleReportListView}
			/>
		</div>
	);

};

export default Services;
